import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Field, FieldPropsExt } from '../../components/field';
import { InputText } from '../../components';
import { DateUtility } from '@mchs/web-core';

export interface DateFieldProps extends FieldPropsExt {
  value: string | null;
  validate?: boolean;
  onChange: (value: string) => void;
}

const DateField: React.FC<DateFieldProps> = (props: DateFieldProps) => {
  const [hasFocus, setHasFocus] = useState<boolean>(false);
  const [validate, setValidate] = useState<boolean>(props.validate ?? false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    props.errorMessage,
  );
  const [value, setValue] = useState<string>(
    props.value === null ? '' : props.value,
  );

  const inputRef = useRef<HTMLInputElement>(null);

  const checkIfRequired = useCallback(() => {
    if (props.required && value.length === 0) {
      setErrorMessage(`${props.label} is required`);
    } else if (!DateUtility.isDateStringValid(value, props.required)) {
      setErrorMessage('Date is invalid (MM/DD/YYYY)');
    } else {
      setErrorMessage(props.errorMessage);
    }
  }, [
    props.required,
    value,
    props.errorMessage,
    props.label,
  ]);

  const formatDateInput = (
    value: string,
    existing: string,
  ): string | undefined => {
    if (/^[0-9/]*$/.test(value)) {
      const regex = /^[0-9\b]+$/;
      const lastDigit = value.slice(value.length - 1, value.length);
      if (
        value.length < 11 &&
        (lastDigit === '' ||
          (value.length === 3 && lastDigit === '/') ||
          (value.length === 6 && lastDigit === '/') ||
          regex.test(lastDigit))
      ) {
        if (value[0] !== '1' && value[0] !== '0') {
          value = '';
        }
        if (parseInt(value[3]) > 3) {
          value = value.substring(0, 3);
        }
        if (value.length === 5 && parseInt(value.substring(3)) > 31) {
          value = value.substring(0, 4);
        }
        if (value.length === 5 && parseInt(value.substring(3)) === 0) {
          value = value.substring(0, 4);
        }
        if (value.length === 2) {
          if (
            parseInt(value.substring(0, 2)) > 12 ||
            parseInt(value.substring(0, 2)) === 0
          ) {
            value = value[0];
          } else if (existing && existing.length === 1) {
            value += '/';
          } else {
            value = value[0];
          }
        }
        if (value.length === 5) {
          if (
            parseInt(value.substring(3, 5)) > 31 ||
            parseInt(value.substring(3, 5)) === 0
          ) {
            value = value.substring(0, 5);
          } else if (existing && existing.length === 4) {
            value += '/';
          } else {
            value = value.substring(0, 4);
          }
        }
        if (
          value.length === 7 &&
          parseInt(lastDigit) !== 2 &&
          parseInt(lastDigit) !== 1
        ) {
          value = value.slice(0, 6);
        }
        if (
          value.length === 8 &&
          parseInt(value.substring(6, 7)) === 2 &&
          parseInt(lastDigit) !== 0
        ) {
          value = value.slice(0, 7);
        }
        return value;
      }
    }
  };

  const onFocus = () => {
    setHasFocus(true);
  };

  const onFieldFocus = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const onBlur = () => {
    setHasFocus(false);
    setValidate(true);
    checkIfRequired();

    if (props.onLostFocus) {
      props.onLostFocus();
    }
  };

  const onChange = (newValue: string) => {
    props.onChange(formatDateInput(newValue, value) ?? value);
  };

  useEffect(() => {
    if (validate) {
      checkIfRequired();
    }
  }, [checkIfRequired, validate]);

  useEffect(() => {
    setValue(props.value === null ? '' : props.value);
  }, [props.value]);

  useEffect(() => {
    setValidate(props.validate ?? false);
  }, [props.validate]);

  return (
    <Field
      {...props}
      errorMessage={errorMessage}
      hasFocus={hasFocus}
      onFocus={onFieldFocus}
    >
      <InputText
        ref={inputRef}
        value={value}
        disabled={props.disabled || props.validating}
        type="text"
        inputMode="numeric"
        placeholder="mm/dd/yyyy"
        onFocus={onFocus}
        onBlur={onBlur}
        onChange={onChange}
      />
    </Field>
  );
};

export default DateField;
